import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const UserDetail = ({ user }) => {
    const [userDetail, setUserDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adminUpdating, setAdminUpdating] = useState(false);
    const { t } = useLanguage();
    const { user: currentUser } = useAuth();

    useEffect(() => {
        if (user) {
            loadUserDetail();
        }
    }, [user]);

    const loadUserDetail = async () => {
        try {
            setLoading(true);
            const response = await userService.getUserDetail(user.id);
            if (response.success) {
                setUserDetail(response.data);
            }
        } catch (error) {
            console.error('Kullanıcı detayı yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAdminToggle = async () => {
        const makeAdmin = !userDetail.user.is_admin;
        if (!window.confirm(t(makeAdmin ? 'confirmGrantAdmin' : 'confirmRevokeAdmin'))) return;
        try {
            setAdminUpdating(true);
            await userService.setUserAdmin(userDetail.user.id, makeAdmin);
            setUserDetail(prev => ({ ...prev, user: { ...prev.user, is_admin: makeAdmin } }));
        } catch (error) {
            window.alert(error.response?.data?.message || t('adminRoleUpdateFailed'));
        } finally {
            setAdminUpdating(false);
        }
    };

    if (!user) {
        return <div>{t('userNotFound')}</div>;
    }

    if (loading) {
        return <div>{t('loading')}</div>;
    }

    if (!userDetail) {
        return <div>{t('userDetailNotFound')}</div>;
    }

    return (
        <div className="user-detail">
            <h2>{t('userDetails')}: {userDetail.user.name}</h2>

            <div className="detail-sections">
                <div className="detail-section">
                    <h3>{t('basicInfo')}</h3>
                    <div className="info-grid">
                        <div className="info-item">
                            <label>{t('id')}:</label>
                            <span>{userDetail.user.id}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('email')}:</label>
                            <span>{userDetail.user.email}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('plan')}:</label>
                            <span className={`plan-badge ${userDetail.user.plan}`}>
                                {userDetail.user.plan}
                            </span>
                        </div>
                        <div className="info-item">
                            <label>{t('status')}:</label>
                            <span className={`status-badge ${userDetail.user.is_active ? 'active' : 'inactive'}`}>
                                {userDetail.user.is_active ? t('active') : t('inactive')}
                            </span>
                        </div>
                        <div className="info-item">
                            <label>{t('adminRole')}:</label>
                            <span className="admin-role">
                                <span className={`status-badge ${userDetail.user.is_admin ? 'active' : 'inactive'}`}>
                                    {userDetail.user.is_admin ? t('active') : t('inactive')}
                                </span>
                                {userDetail.user.id !== currentUser?.id && (
                                    <button
                                        className={`admin-toggle-btn ${userDetail.user.is_admin ? 'revoke' : 'grant'}`}
                                        onClick={handleAdminToggle}
                                        disabled={adminUpdating}
                                    >
                                        {userDetail.user.is_admin ? t('revokeAdmin') : t('grantAdmin')}
                                    </button>
                                )}
                            </span>
                        </div>
                        <div className="info-item">
                            <label>{t('registrationDate')}:</label>
                            <span>{new Date(userDetail.user.created_at).toLocaleDateString('tr-TR')}</span>
                        </div>
                    </div>
                </div>

                <div className="detail-section">
                    <h3>{t('pomodoroSettings')}</h3>
                    <div className="info-grid">
                        <div className="info-item">
                            <label>{t('workDuration')}:</label>
                            <span>{userDetail.profile.workDuration} {t('minutes')}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('breakDuration')}:</label>
                            <span>{userDetail.profile.breakDuration} {t('minutes')}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('longBreak')}:</label>
                            <span>{userDetail.profile.longBreakDuration} {t('minutes')}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('sessionsCount')}:</label>
                            <span>{userDetail.profile.sessionsBeforeLongBreak}</span>
                        </div>
                    </div>
                </div>

                <div className="detail-section">
                    <h3>{t('aiUsageStats')}</h3>
                    <div className="info-grid">
                        <div className="info-item">
                            <label>{t('dailyMessages')}:</label>
                            <span>{userDetail.statistics?.aiUsage?.dailyMessages || 0}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('dailyTokens')}:</label>
                            <span>{userDetail.statistics?.aiUsage?.dailyTokens || 0}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('totalMessages')}:</label>
                            <span>{userDetail.statistics?.aiUsage?.totalMessages || 0}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('totalTokens')}:</label>
                            <span>{userDetail.statistics?.aiUsage?.totalTokens || 0}</span>
                        </div>
                    </div>
                </div>

                <div className="detail-section">
                    <h3>{t('recentSessions')}</h3>
                    <div className="sessions-list">
                        {userDetail.recentSessions?.map((session, index) => (
                            <div key={index} className="session-item">
                                <span className="session-type">{session.session_type}</span>
                                <span className="session-time">
                                    {new Date(session.start_time).toLocaleString('tr-TR')}
                                </span>
                                <span className="session-duration">{Math.round((session.planned_duration || 0) / 60)} {t('minutes')}</span>
                                <span className={`session-status ${session.completed ? 'completed' : 'incomplete'}`}>
                                    {session.completed ? t('completed') : t('incomplete')}
                                </span>
                            </div>
                        ))}
                        {(!userDetail.recentSessions || userDetail.recentSessions.length === 0) && (
                            <div className="no-sessions">{t('noSessions')}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetail;